import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { renderMultiSectionDigitalClockTimeView } from '@mui/x-date-pickers/timeViewRenderers';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import dayjs from 'dayjs';
import { SyntheticEvent, useEffect, useMemo, useRef, useState } from 'react';
import { useField } from 'react-final-form';

dayjs.extend(customParseFormat);

const normalizeDateTime = (val: any) => {
  if (!val) return null;

  if (dayjs.isDayjs(val)) {
    return val.isValid() ? val : null;
  }

  const parsed = dayjs(val, ['YYYY-MM-DD HH:mm', 'YYYY-MM-DDTHH:mm', 'DD/MM/YYYY HH:mm'], true);
  return parsed.isValid() ? parsed : null;
};

export const DateTimePickerAdapter = ({ input: { name, value }, setValue, ...rest }: any) => {
  const { input, meta } = useField(name);
  const Picker = DateTimePicker as any;
  const [draft, setDraft] = useState(normalizeDateTime(value));
  const draftRef = useRef(normalizeDateTime(value));

  const parsedValue = useMemo(() => normalizeDateTime(value), [value]);

  useEffect(() => {
    setDraft(parsedValue);
    draftRef.current = parsedValue;
  }, [parsedValue]);

  const commit = (newValue: any, options?: { allowNull?: boolean }) => {
    const allowNull = options?.allowNull ?? false;

    if (newValue === null) {
      if (allowNull) {
        input.onChange(null);
        if (setValue) setValue(null);
      }
      return;
    }

    if (dayjs.isDayjs(newValue) && newValue.isValid()) {
      input.onChange(newValue.format('YYYY-MM-DD HH:mm'));
      if (setValue) setValue(newValue);
    }
  };

  const stopPropagation = (event: SyntheticEvent) => {
    event.stopPropagation();
  };

  const handleChange = (newValue: any) => {
    setDraft(newValue);
    draftRef.current = newValue;
  };

  const handleAccept = (newValue: any) => {
    commit(newValue, { allowNull: true });
  };

  const handleBlur = (event: any) => {
    commit(draftRef.current);
    input.onBlur(event);
    rest?.onBlur?.(event);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="it">
      <Picker
        onChange={handleChange}
        onAccept={handleAccept}
        value={draft}
        ampm={false}
        autoFocus={false}
        format="DD/MM/YYYY HH:mm"
        timeSteps={{ hours: 1, minutes: 1 }}
        viewRenderers={{
          hours: renderMultiSectionDigitalClockTimeView,
          minutes: renderMultiSectionDigitalClockTimeView,
          seconds: null
        }}
        keepOpenDuringFieldFocus
        slotProps={{
          popper: {
            onClick: stopPropagation,
            onMouseDown: stopPropagation
          },
          textField: {
            ...rest,
            onClick: (event: SyntheticEvent) => {
              stopPropagation(event);
              rest?.onClick?.(event);
            },
            onMouseDown: (event: SyntheticEvent) => {
              stopPropagation(event);
              rest?.onMouseDown?.(event);
            },
            onKeyDown: (event: SyntheticEvent) => {
              stopPropagation(event);
              rest?.onKeyDown?.(event);
            },
            onKeyUp: (event: SyntheticEvent) => {
              stopPropagation(event);
              rest?.onKeyUp?.(event);
            },
            onBlur: handleBlur,
            helperText: meta.touched ? meta.error : '',
            error: !!(meta.error && meta.touched),
            fullWidth: true
          }
        }}
      />
    </LocalizationProvider>
  );
};
